/**
 * tenderApi.ts — TanStack Query hooks for the tender domain backend
 * (tender-api, port 8000 — live). This is the Ingest/Projects surface's data
 * layer (Track A): it replaces the mock projects table in IngestPage with real,
 * RLS-scoped data from the live service.
 *
 * Built on the existing `api.cmeinsight` client (api.ts), which is already
 * pre-bound to the tender audience (config.auth0Audience). Mirrors the proven
 * kbApi.ts pattern: wire DTOs (api/schemas.py) → frontend models, absorbing the
 * convention mismatches at this seam so the UI never sees them:
 *   - status is a snake_case enum on the wire ("audit"); the UI shows an Italian
 *     display label ("Audit in corso") via STATUS_LABEL.
 *   - timestamps arrive as ISO datetimes; the UI wants a relative label
 *     ("2 ore fa") computed by `relativeTime`.
 *   - the CME total (total_value) is null until an analysis has produced line
 *     items — the UI renders "—" in that case.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, useApi } from "./api";

// ── Projects ─────────────────────────────────────────────────────────────────

/** Backend status enum (api/models.py ProjectStatus). */
export type TenderProjectStatus = "ingest" | "audit" | "strategy" | "completed" | "archived";

/** Italian display label per backend status, for the dashboard pills. */
export const STATUS_LABEL: Record<TenderProjectStatus, string> = {
  ingest: "Ingest",
  audit: "Audit in corso",
  strategy: "Strategia",
  completed: "Completato",
  archived: "Archiviato",
};

/** Wire shape of ProjectSummary (api/schemas.py). */
interface ProjectSummaryDto {
  id: string;
  name: string;
  city: string | null;
  status: string;
  surface_m2: number | null;
  created_by_sub: string;
  created_at: string;
  updated_at: string;
  document_count: number;
  /** Total value of the CME (€); null until an analysis has run. */
  total_value: number | null;
}

interface ProjectListDto {
  projects: ProjectSummaryDto[];
  total: number;
  limit: number;
  offset: number;
}

export interface TenderProject {
  id: string;
  name: string;
  city: string | null;
  status: TenderProjectStatus;
  /** Italian display label for `status`. */
  statusLabel: string;
  surfaceM2: number | null;
  /** Total value of the CME in €, or null if the project hasn't been analyzed. */
  totalValue: number | null;
  documentCount: number;
  createdBySub: string;
  createdAt: string;
  updatedAt: string;
  /** Relative "updated" label ("2 ore fa", "ieri", "3 giorni fa"). */
  updatedLabel: string;
}

const KNOWN_STATUS = new Set<TenderProjectStatus>([
  "ingest",
  "audit",
  "strategy",
  "completed",
  "archived",
]);

function resolveStatus(raw: string): TenderProjectStatus {
  return KNOWN_STATUS.has(raw as TenderProjectStatus) ? (raw as TenderProjectStatus) : "ingest";
}

/**
 * Relative-time label in Italian, matching the prototype's tone ("2 ore fa",
 * "ieri", "3 giorni fa", "1 settimana fa"). `now` is injectable for tests.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  const mins = Math.floor(secs / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  if (secs < 60) return "ora";
  if (mins < 60) return mins === 1 ? "1 minuto fa" : `${mins} minuti fa`;
  if (hours < 24) return hours === 1 ? "1 ora fa" : `${hours} ore fa`;
  if (days === 1) return "ieri";
  if (days < 7) return `${days} giorni fa`;
  if (weeks < 5) return weeks === 1 ? "1 settimana fa" : `${weeks} settimane fa`;
  return new Date(iso).toISOString().slice(0, 10);
}

export function toTenderProject(dto: ProjectSummaryDto, now?: number): TenderProject {
  const status = resolveStatus(dto.status);
  return {
    id: dto.id,
    name: dto.name,
    city: dto.city,
    status,
    statusLabel: STATUS_LABEL[status],
    surfaceM2: dto.surface_m2,
    totalValue: dto.total_value,
    documentCount: dto.document_count,
    createdBySub: dto.created_by_sub,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    updatedLabel: relativeTime(dto.updated_at, now),
  };
}

export interface ProjectListFilters {
  status?: TenderProjectStatus;
  q?: string;
  includeArchived?: boolean;
}

/** Live projects list for the active tenant (RLS-scoped server-side). */
export function useProjects(filters: ProjectListFilters = {}): UseQueryResult<TenderProject[]> {
  const api = useApi();
  const { status, q, includeArchived } = filters;
  return useQuery<TenderProject[]>({
    queryKey: ["tender", "projects", { status, q, includeArchived }],
    queryFn: async ({ signal }): Promise<TenderProject[]> => {
      const res = await api.cmeinsight.get<ProjectListDto>("/v1/projects", {
        signal,
        params: { status, q, include_archived: includeArchived },
      });
      return res.projects.map((p) => toTenderProject(p));
    },
  });
}

/** Fetch one project. */
export function useProject(projectId: string | null): UseQueryResult<TenderProject> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<TenderProject> => {
      const dto = await api.cmeinsight.get<ProjectSummaryDto>(`/v1/projects/${projectId}`, {
        signal,
      });
      return toTenderProject(dto);
    },
  });
}

// ── Dashboard stats ──────────────────────────────────────────────────────────

/** Wire shape of ProjectStats (api/schemas.py) — org-wide dashboard aggregates. */
interface ProjectStatsDto {
  active_projects: number;
  analyzed_projects: number;
  total_cme_value: number;
  total_documents: number;
  open_discrepancies: number;
  critical_discrepancies: number;
}

export interface DashboardStats {
  activeProjects: number;
  analyzedProjects: number;
  totalCmeValue: number;
  totalDocuments: number;
  openDiscrepancies: number;
  criticalDiscrepancies: number;
}

export function toDashboardStats(dto: ProjectStatsDto): DashboardStats {
  return {
    activeProjects: dto.active_projects,
    analyzedProjects: dto.analyzed_projects,
    totalCmeValue: dto.total_cme_value,
    totalDocuments: dto.total_documents,
    openDiscrepancies: dto.open_discrepancies,
    criticalDiscrepancies: dto.critical_discrepancies,
  };
}

/** Org-wide aggregates for the workspace dashboard stat cards (GET /v1/projects/stats). */
export function useDashboardStats(): UseQueryResult<DashboardStats> {
  const api = useApi();
  return useQuery<DashboardStats>({
    queryKey: ["tender", "projects", "stats"],
    queryFn: async ({ signal }): Promise<DashboardStats> => {
      const dto = await api.cmeinsight.get<ProjectStatsDto>("/v1/projects/stats", { signal });
      return toDashboardStats(dto);
    },
  });
}

export interface CreateProjectInput {
  name: string;
  city?: string;
  surfaceM2?: number;
}

/** Create a tender project (POST /v1/projects). Refreshes the list on success. */
export function useCreateProject(): UseMutationResult<TenderProject, Error, CreateProjectInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, city, surfaceM2 }: CreateProjectInput): Promise<TenderProject> => {
      const dto = await api.cmeinsight.post<ProjectSummaryDto>("/v1/projects", {
        body: { name, city, surface_m2: surfaceM2 },
      });
      return toTenderProject(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

/** Soft-delete a project (DELETE /v1/projects/{id} → status archived). */
export function useDeleteProject(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.cmeinsight.del<void>(`/v1/projects/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

// ── Documents ──────────────────────────────────────────────────────────────

/** Which input a document plays in the analyze pipeline (api/models.py DocumentRole). */
export type TenderDocRole = "cme" | "full_price" | "np" | "info" | "regional" | "drawing" | "other";

/** Italian display label per document role, for the upload UI. */
export const DOC_ROLE_LABEL: Record<TenderDocRole, string> = {
  cme: "CME (computo metrico)",
  full_price: "Elenco Prezzi (EP)",
  np: "Nuovi Prezzi (AP/NP)",
  info: "Relazione",
  regional: "Listino regionale (.xlsx)",
  drawing: "Disegno (allegato)",
  other: "Altro",
};

/**
 * Best-effort role guess from a filename, mirroring the backend's _infer_role
 * (api/documents.py) so the upload UI's default role matches what the server
 * would infer. The user can override it per file; the explicit role wins.
 * Short keywords (EP/AP/NP) match only as whole tokens — bare substrings turned
 * "deposito" into full_price and "capitolato" into np.
 */
export function guessRole(filename: string): TenderDocRole {
  const name = filename.toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  if (ext === ".xlsx" || ext === ".xls") return "regional";
  if (ext === ".dwg") return "drawing";
  if (name.includes("cme")) return "cme";
  if (name.includes("relazione")) return "info";
  const tokens = new Set(name.split(/[^a-z0-9]+/));
  if (tokens.has("ep")) return "full_price";
  if (tokens.has("ap") || tokens.has("np")) return "np";
  return "other";
}

/**
 * The document roles an analysis cannot run without — mirror of the Python
 * REQUIRED_ROLES in tender-api analysis/extract.py (keep the two in sync).
 * Used to gate auto-start after project creation and to explain what's missing.
 */
export const REQUIRED_ANALYZE_ROLES: readonly TenderDocRole[] = [
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
];

interface DocumentSummaryDto {
  id: string;
  project_id: string;
  original_filename: string;
  doc_type: string;
  role: string;
  size_bytes: number;
  status: string;
  uploaded_by_sub: string;
  created_at: string;
}

interface DocumentListDto {
  documents: DocumentSummaryDto[];
  total: number;
}

export interface TenderDocument {
  id: string;
  projectId: string;
  filename: string;
  docType: string;
  role: TenderDocRole;
  roleLabel: string;
  sizeBytes: number;
  status: string;
  uploadedBySub: string;
  createdAt: string;
}

const KNOWN_ROLE = new Set<TenderDocRole>([
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
  "drawing",
  "other",
]);

export function toTenderDocument(dto: DocumentSummaryDto): TenderDocument {
  const role = KNOWN_ROLE.has(dto.role as TenderDocRole) ? (dto.role as TenderDocRole) : "other";
  return {
    id: dto.id,
    projectId: dto.project_id,
    filename: dto.original_filename,
    docType: dto.doc_type,
    role,
    roleLabel: DOC_ROLE_LABEL[role],
    sizeBytes: dto.size_bytes,
    status: dto.status,
    uploadedBySub: dto.uploaded_by_sub,
    createdAt: dto.created_at,
  };
}

/** Documents attached to a project (GET /v1/projects/{id}/documents). */
export function useProjectDocuments(projectId: string | null): UseQueryResult<TenderDocument[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "documents"],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<TenderDocument[]> => {
      const res = await api.cmeinsight.get<DocumentListDto>(`/v1/projects/${projectId}/documents`, {
        signal,
      });
      return res.documents.map(toTenderDocument);
    },
  });
}

export interface UploadDocumentInput {
  projectId: string;
  file: File;
  /** Pipeline role; the backend auto-detects from the filename if omitted. */
  role?: TenderDocRole;
}

/** Upload a document to a project (multipart → POST /v1/projects/{id}/documents). */
export function useUploadDocument(): UseMutationResult<TenderDocument, Error, UploadDocumentInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, file, role }: UploadDocumentInput): Promise<TenderDocument> => {
      const form = new FormData();
      form.append("file", file);
      if (role) form.append("role", role);
      const dto = await api.cmeinsight.post<DocumentSummaryDto>(
        `/v1/projects/${projectId}/documents`,
        { formData: form },
      );
      return toTenderDocument(dto);
    },
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

/** Delete a document (DELETE /v1/documents/{id} → status archived). */
export function useDeleteDocument(): UseMutationResult<
  void,
  Error,
  { id: string; projectId: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; projectId: string }): Promise<void> =>
      api.cmeinsight.del<void>(`/v1/documents/${id}`),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "documents"] });
      void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
    },
  });
}

// ── Audit: categories / line-items / discrepancies ───────────────────────────
// The analyze job (Phase-A) populates these per project. Backend enums stay in
// English on the wire; the audit UI's helpers.ts applies the Italian labels.

export type DiscrepancyType = "price_mismatch" | "qty_mismatch" | "desc_mismatch";
export type Severity = "err" | "warn";
/** Backend has 4 states; the UI surfaces 3 (open+unresolved both read as "non risolto"). */
export type DiscrepancyStatus = "open" | "in_review" | "resolved" | "unresolved";

// --- categories ---
interface CategorySubDto {
  id: string;
  code: string | null;
  name: string;
  cost: number | null;
  eur_m2: number | null;
  pct: number | null;
}
interface CategoryDto extends CategorySubDto {
  is_uncategorized: boolean;
  subs: CategorySubDto[];
}

export interface AuditCategorySub {
  id: string;
  code: string | null;
  name: string;
  cost: number | null;
  eurM2: number | null;
  pct: number | null;
}
export interface AuditCategory extends AuditCategorySub {
  isUncategorized: boolean;
  subs: AuditCategorySub[];
}

function toCategorySub(dto: CategorySubDto): AuditCategorySub {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    cost: dto.cost,
    eurM2: dto.eur_m2,
    pct: dto.pct,
  };
}
export function toAuditCategory(dto: CategoryDto): AuditCategory {
  return {
    ...toCategorySub(dto),
    isUncategorized: dto.is_uncategorized,
    subs: dto.subs.map(toCategorySub),
  };
}

/** Live category tree for a project (GET /v1/projects/{id}/categories). */
export function useAuditCategories(projectId: string | null): UseQueryResult<AuditCategory[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "project", projectId, "categories"],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<AuditCategory[]> => {
      const res = await api.cmeinsight.get<{ categories: CategoryDto[] }>(
        `/v1/projects/${projectId}/categories`,
        { signal },
      );
      return res.categories.map(toAuditCategory);
    },
  });
}

// --- line items + discrepancies ---
interface DiscrepancyBriefDto {
  id: string;
  type: DiscrepancyType;
  severity: Severity;
  status: DiscrepancyStatus;
  note: string;
  compare_value: string | null;
  compare_delta: string | null;
  compare_source: string | null;
}
interface LineItemDto {
  id: string;
  code: string | null;
  description: string;
  qty: number;
  unit: string | null;
  unit_price: number;
  total: number;
  pct_budget: number | null;
  category_id: string | null;
  suggested_category_id: string | null;
  discrepancy: DiscrepancyBriefDto | null;
}

export interface AuditDiscrepancyBrief {
  id: string;
  type: DiscrepancyType;
  severity: Severity;
  status: DiscrepancyStatus;
  note: string;
  compareValue: string | null;
  compareDelta: string | null;
  compareSource: string | null;
}
export interface AuditLineItem {
  id: string;
  code: string | null;
  description: string;
  qty: number;
  unit: string | null;
  unitPrice: number;
  total: number;
  pctBudget: number | null;
  categoryId: string | null;
  suggestedCategoryId: string | null;
  discrepancy: AuditDiscrepancyBrief | null;
}

function toDiscrepancyBrief(dto: DiscrepancyBriefDto): AuditDiscrepancyBrief {
  return {
    id: dto.id,
    type: dto.type,
    severity: dto.severity,
    status: dto.status,
    note: dto.note,
    compareValue: dto.compare_value,
    compareDelta: dto.compare_delta,
    compareSource: dto.compare_source,
  };
}
export function toAuditLineItem(dto: LineItemDto): AuditLineItem {
  return {
    id: dto.id,
    code: dto.code,
    description: dto.description,
    qty: dto.qty,
    unit: dto.unit,
    unitPrice: dto.unit_price,
    total: dto.total,
    pctBudget: dto.pct_budget,
    categoryId: dto.category_id,
    suggestedCategoryId: dto.suggested_category_id,
    discrepancy: dto.discrepancy ? toDiscrepancyBrief(dto.discrepancy) : null,
  };
}

export interface LineItemFilters {
  hasDiscrepancy?: boolean;
  severity?: Severity;
  categoryId?: string;
  uncategorized?: boolean;
}

/** Live line items for a project, with optional filters (GET /v1/projects/{id}/line-items). */
export function useAuditLineItems(
  projectId: string | null,
  filters: LineItemFilters = {},
): UseQueryResult<AuditLineItem[]> {
  const api = useApi();
  const { hasDiscrepancy, severity, categoryId, uncategorized } = filters;
  return useQuery({
    queryKey: ["tender", "project", projectId, "line-items", filters],
    enabled: !!projectId,
    queryFn: async ({ signal }): Promise<AuditLineItem[]> => {
      const res = await api.cmeinsight.get<{ line_items: LineItemDto[]; total: number }>(
        `/v1/projects/${projectId}/line-items`,
        {
          signal,
          params: {
            has_discrepancy: hasDiscrepancy,
            severity,
            category_id: categoryId,
            uncategorized,
          },
        },
      );
      return res.line_items.map(toAuditLineItem);
    },
  });
}

// --- discrepancy detail (with comments) ---
interface CommentDto {
  id: string;
  author_sub: string;
  text: string;
  created_at: string;
}
interface DiscrepancyDetailDto {
  id: string;
  project_id: string;
  line_item_id: string;
  type: DiscrepancyType;
  severity: Severity;
  status: DiscrepancyStatus;
  note: string;
  compare_value: string | null;
  compare_delta: string | null;
  compare_source: string | null;
  created_at: string;
  comments: CommentDto[];
}

export interface AuditComment {
  id: string;
  authorSub: string;
  text: string;
  createdAt: string;
}
export interface AuditDiscrepancyDetail extends AuditDiscrepancyBrief {
  projectId: string;
  lineItemId: string;
  createdAt: string;
  comments: AuditComment[];
}

function toComment(dto: CommentDto): AuditComment {
  return { id: dto.id, authorSub: dto.author_sub, text: dto.text, createdAt: dto.created_at };
}
export function toDiscrepancyDetail(dto: DiscrepancyDetailDto): AuditDiscrepancyDetail {
  return {
    ...toDiscrepancyBrief(dto),
    projectId: dto.project_id,
    lineItemId: dto.line_item_id,
    createdAt: dto.created_at,
    comments: dto.comments.map(toComment),
  };
}

/** One discrepancy with its comment thread (GET /v1/discrepancies/{id}). */
export function useDiscrepancy(
  discrepancyId: string | null,
): UseQueryResult<AuditDiscrepancyDetail> {
  const api = useApi();
  return useQuery({
    queryKey: ["tender", "discrepancy", discrepancyId],
    enabled: !!discrepancyId,
    queryFn: async ({ signal }): Promise<AuditDiscrepancyDetail> => {
      const dto = await api.cmeinsight.get<DiscrepancyDetailDto>(
        `/v1/discrepancies/${discrepancyId}`,
        { signal },
      );
      return toDiscrepancyDetail(dto);
    },
  });
}

// --- audit mutations ---

/** Assign (or clear, with null) one line item's category (PATCH /v1/line-items/{id}). */
export function useCategorizeLineItem(
  projectId: string,
): UseMutationResult<AuditLineItem, Error, { lineItemId: string; categoryId: string | null }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ lineItemId, categoryId }): Promise<AuditLineItem> => {
      const dto = await api.cmeinsight.patch<LineItemDto>(`/v1/line-items/${lineItemId}`, {
        body: { category_id: categoryId },
      });
      return toAuditLineItem(dto);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "line-items"] });
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "categories"] });
    },
  });
}

/** Bulk-assign a category to many line items (PATCH /v1/line-items/bulk). */
export function useBulkCategorizeLineItems(
  projectId: string,
): UseMutationResult<{ updated: number }, Error, { ids: string[]; categoryId: string | null }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, categoryId }): Promise<{ updated: number }> =>
      api.cmeinsight.patch<{ updated: number }>(`/v1/line-items/bulk`, {
        body: { ids, category_id: categoryId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "line-items"] });
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "categories"] });
    },
  });
}

/** Update a discrepancy's status (PATCH /v1/discrepancies/{id}). */
export function useSetDiscrepancyStatus(
  projectId: string,
): UseMutationResult<
  AuditDiscrepancyDetail,
  Error,
  { discrepancyId: string; status: DiscrepancyStatus }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ discrepancyId, status }): Promise<AuditDiscrepancyDetail> => {
      const dto = await api.cmeinsight.patch<DiscrepancyDetailDto>(
        `/v1/discrepancies/${discrepancyId}`,
        { body: { status } },
      );
      return toDiscrepancyDetail(dto);
    },
    onSuccess: (_data, { discrepancyId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "discrepancy", discrepancyId] });
      void qc.invalidateQueries({ queryKey: ["tender", "project", projectId, "line-items"] });
    },
  });
}

/** Add a comment to a discrepancy (POST /v1/discrepancies/{id}/comments). */
export function useAddDiscrepancyComment(): UseMutationResult<
  AuditComment,
  Error,
  { discrepancyId: string; text: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ discrepancyId, text }): Promise<AuditComment> => {
      const dto = await api.cmeinsight.post<CommentDto>(
        `/v1/discrepancies/${discrepancyId}/comments`,
        { body: { text } },
      );
      return toComment(dto);
    },
    onSuccess: (_data, { discrepancyId }) => {
      void qc.invalidateQueries({ queryKey: ["tender", "discrepancy", discrepancyId] });
    },
  });
}

// ── Analyze job ──────────────────────────────────────────────────────────────

/** Backend job lifecycle (api/models.py JobStatus). */
export type AnalysisJobStatus = "queued" | "running" | "completed" | "failed";

/** Wire shape of JobCreated / JobStatusOut (api/schemas.py) — the poll superset.
 * Exported for the JobsWatcher's terminal-state fetch. */
export interface JobDto {
  id: string;
  project_id: string;
  status: string;
  progress?: number;
  error?: string | null;
  result?: Record<string, unknown> | null;
}

export interface AnalysisJob {
  id: string;
  projectId: string;
  status: AnalysisJobStatus;
  /** 0–100, as reported by the runner. */
  progress: number;
  error: string | null;
  result: Record<string, unknown> | null;
}

const KNOWN_JOB_STATUS = new Set<AnalysisJobStatus>(["queued", "running", "completed", "failed"]);

export function toAnalysisJob(dto: JobDto): AnalysisJob {
  const status = KNOWN_JOB_STATUS.has(dto.status as AnalysisJobStatus)
    ? (dto.status as AnalysisJobStatus)
    : "queued";
  return {
    id: dto.id,
    projectId: dto.project_id,
    status,
    progress: dto.progress ?? 0,
    error: dto.error ?? null,
    result: dto.result ?? null,
  };
}

/** Kick off the real Phase-A analysis (POST /v1/projects/{id}/analyze → 202 + job).
 * The backend dedups: if a run is already queued/running it returns THAT job. */
export function useStartAnalysis(): UseMutationResult<AnalysisJob, Error, string> {
  const api = useApi();
  return useMutation({
    mutationFn: async (projectId: string): Promise<AnalysisJob> => {
      const dto = await api.cmeinsight.post<JobDto>(`/v1/projects/${projectId}/analyze`);
      return toAnalysisJob(dto);
    },
  });
}

/**
 * Poll cadence per job status: brisk while queued (startup is fast), relaxed
 * while running (the LLM phases take hours), stopped when terminal. Pure +
 * exported for unit tests.
 */
export function jobPollInterval(status: AnalysisJobStatus | undefined): number | false {
  if (status === "completed" || status === "failed") return false;
  if (status === "running") return 15_000;
  return 2_500;
}

/**
 * If `err` is the analyze pre-flight 422, return the missing document roles
 * (the UI maps them to Italian labels via DOC_ROLE_LABEL); null otherwise.
 */
export function missingRolesFromError(err: unknown): TenderDocRole[] | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const detail = (err.body as { detail?: { missing_roles?: unknown } } | null)?.detail;
  const roles = detail?.missing_roles;
  if (!Array.isArray(roles)) return null;
  return roles.filter((r): r is TenderDocRole => typeof r === "string");
}

/**
 * Poll an analysis job (GET /v1/jobs/{id}) until it reaches a terminal state.
 * On completion the tender caches are invalidated so the header / audit tree /
 * dashboard pick up the freshly-persisted line items.
 */
export function useAnalysisJob(jobId: string | null): UseQueryResult<AnalysisJob> {
  const api = useApi();
  const qc = useQueryClient();
  return useQuery<AnalysisJob>({
    queryKey: ["tender", "job", jobId],
    enabled: !!jobId,
    refetchInterval: (query) => jobPollInterval(query.state.data?.status),
    queryFn: async ({ signal }): Promise<AnalysisJob> => {
      const job = toAnalysisJob(await api.cmeinsight.get<JobDto>(`/v1/jobs/${jobId}`, { signal }));
      if (job.status === "completed") {
        // New categories / line items / totals exist — refresh the domain.
        void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
        void qc.invalidateQueries({ queryKey: ["tender", "project", job.projectId] });
      }
      return job;
    },
  });
}

/**
 * Most recent analyze job for a project (GET /v1/projects/{id}/jobs/latest) —
 * lets a reloaded/reconnected client rediscover an in-flight or finished run.
 * Resolves to null when the project has never been analyzed (backend 404).
 * While the rediscovered job is active it keeps polling on the same cadence,
 * and invalidates the tender caches when it completes.
 */
export function useLatestAnalysisJob(projectId: string | null): UseQueryResult<AnalysisJob | null> {
  const api = useApi();
  const qc = useQueryClient();
  return useQuery<AnalysisJob | null>({
    queryKey: ["tender", "project", projectId, "latest-job"],
    enabled: !!projectId,
    refetchInterval: (query) => {
      const job = query.state.data;
      return job ? jobPollInterval(job.status) : false;
    },
    queryFn: async ({ signal }): Promise<AnalysisJob | null> => {
      try {
        const dto = await api.cmeinsight.get<JobDto>(`/v1/projects/${projectId}/jobs/latest`, {
          signal,
        });
        const job = toAnalysisJob(dto);
        if (job.status === "completed") {
          void qc.invalidateQueries({ queryKey: ["tender", "projects"] });
          void qc.invalidateQueries({ queryKey: ["tender", "project", job.projectId] });
        }
        return job;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null; // never analyzed
        throw e;
      }
    },
  });
}

/**
 * The org's queued/running analyze jobs (GET /v1/jobs/active) — the feed for
 * the app-wide completion watcher. Light steady poll while the app is open.
 */
export function useActiveJobs(): UseQueryResult<AnalysisJob[]> {
  const api = useApi();
  return useQuery<AnalysisJob[]>({
    queryKey: ["tender", "jobs", "active"],
    refetchInterval: 15_000,
    queryFn: async ({ signal }): Promise<AnalysisJob[]> => {
      const rows = await api.cmeinsight.get<JobDto[]>("/v1/jobs/active", { signal });
      return rows.map(toAnalysisJob);
    },
  });
}
